import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Download, KeyRound, Pencil, Plus, Server, Trash2, Zap } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import type { Provider } from "@/api/generated/Provider"
import type { ProviderTest } from "@/api/generated/ProviderTest"
import { useDeleteProvider, useProviders, useTestProvider } from "@/api/queries/providers"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { ImportModelsDialog } from "@/features/studio/import-models-dialog"
import { useProviderKindLabel } from "@/features/studio/provider-kind"
import { ProviderSheet } from "@/features/studio/provider-sheet"
import { formatNumber } from "@/lib/format"

interface ProvidersSearch {
  new?: boolean
}

export const Route = createFileRoute("/_app/studio/providers")({
  staticData: { titleKey: "nav.items.providers" },
  validateSearch: (s: Record<string, unknown>): ProvidersSearch => ({
    new: s.new === true || s.new === "true" ? true : undefined,
  }),
  component: ProvidersPage,
})

type TestState = { pending: true } | ({ pending: false } & ProviderTest)

function ProvidersPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const providers = useProviders()
  const remove = useDeleteProvider()
  const test = useTestProvider()
  const confirm = useConfirm()
  const [editing, setEditing] = useState<Provider>()
  const [sheetOpen, setSheetOpen] = useState(false)
  const [importing, setImporting] = useState<Provider>()
  const [tests, setTests] = useState<Record<number, TestState>>({})
  const kindLabel = useProviderKindLabel()

  const open = sheetOpen || !!search.new
  const setOpen = (o: boolean) => {
    setSheetOpen(o)
    if (!o) {
      setEditing(undefined)
      if (search.new) void navigate({ search: {}, replace: true })
    }
  }

  const runTest = async (p: Provider) => {
    setTests((s) => ({ ...s, [p.id]: { pending: true } }))
    try {
      const r = await test.mutateAsync(p.id)
      setTests((s) => ({ ...s, [p.id]: { pending: false, ...r } }))
      if (r.ok)
        toast.success(t("studio.providers.test.ok", { name: p.name, ms: r.latency_ms, count: r.model_count }))
      else
        toast.error(t("studio.providers.test.failed", { name: p.name }), {
          description: r.error ?? undefined,
        })
    } catch {
      setTests((s) => {
        const { [p.id]: _, ...rest } = s
        return rest
      })
    }
  }

  const confirmDelete = (p: Provider) =>
    confirm({
      title: t("studio.providers.deleteTitle", { name: p.name }),
      description:
        p.model_count > 0
          ? t("studio.providers.deleteInUse", { count: p.model_count })
          : t("studio.providers.deleteDescription"),
      confirmLabel: t("studio.providers.delete"),
      destructive: true,
      onConfirm: () => remove.mutateAsync(p),
    })

  const columns = useMemo(
    () =>
      createColumns<Provider>((c) => [
        c.accessor("name", {
          header: t("studio.providers.columns.name"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="min-w-0">
              <div className="truncate font-medium">{row.original.name}</div>
              <code className="text-muted-foreground block max-w-80 truncate font-mono text-xs">
                {row.original.base_url || t("studio.providers.defaultEndpoint")}
              </code>
            </div>
          ),
        }),
        c.accessor("kind", {
          header: t("studio.providers.columns.kind"),
          cell: ({ getValue }) => <StatusBadge>{kindLabel(getValue())}</StatusBadge>,
        }),
        c.accessor("has_api_key", {
          header: t("studio.providers.columns.apiKey"),
          cell: ({ row }) =>
            row.original.has_api_key ? (
              <span className="text-muted-foreground inline-flex items-center gap-1.5 font-mono text-xs">
                <KeyRound className="size-3" />
                {row.original.api_key_hint}
              </span>
            ) : (
              <span className="text-muted-foreground text-xs">{t("studio.providers.noKey")}</span>
            ),
        }),
        c.accessor("model_count", {
          header: t("studio.providers.columns.models"),
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatNumber(getValue())}</span>,
        }),
        c.display({
          id: "status",
          header: t("studio.providers.columns.status"),
          meta: { label: t("studio.providers.columns.status") },
          cell: ({ row }) => {
            const s = tests[row.original.id]
            if (!s) return <span className="text-muted-foreground text-xs">—</span>
            if (s.pending) return <Spinner className="text-muted-foreground size-3.5" />
            return s.ok ? (
              <StatusBadge tone="success" dot>
                {t("studio.providers.test.online", { ms: s.latency_ms })}
              </StatusBadge>
            ) : (
              <Tooltip>
                <TooltipTrigger render={<span />}>
                  <StatusBadge tone="danger" dot>
                    {t("studio.providers.test.offline")}
                  </StatusBadge>
                </TooltipTrigger>
                <TooltipContent className="max-w-80 break-words">{s.error}</TooltipContent>
              </Tooltip>
            )
          },
        }),
        c.accessor("updated_at", {
          header: t("studio.providers.columns.updated"),
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t, tests, kindLabel],
  )

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("studio.providers.title")}
        description={t("studio.providers.description")}
        icon={Server}
        actions={
          <Button onClick={() => setSheetOpen(true)} data-testid="new-provider">
            <Plus />
            {t("studio.providers.new")}
          </Button>
        }
      />
      <DataTable
        label={t("studio.providers.title")}
        data={providers.data}
        columns={columns}
        getRowId={(p) => String(p.id)}
        loading={providers.isPending}
        error={providers.error}
        onRetry={() => void providers.refetch()}
        searchable
        searchPlaceholder={t("studio.providers.search")}
        initialSorting={[{ id: "name", desc: false }]}
        onRowClick={(p) => {
          setEditing(p)
          setSheetOpen(true)
        }}
        rowActions={(p) => (
          <>
            <DropdownMenuItem onClick={() => void runTest(p)}>
              <Zap />
              {t("studio.providers.test.action")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setImporting(p)}>
              <Download />
              {t("studio.providers.import.action")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setEditing(p)
                setSheetOpen(true)
              }}
            >
              <Pencil />
              {t("common.edit")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onClick={() => void confirmDelete(p)}>
              <Trash2 />
              {t("studio.providers.delete")}
            </DropdownMenuItem>
          </>
        )}
        emptyState={
          <EmptyState
            variant="plain"
            icon={Server}
            title={t("studio.providers.empty.title")}
            description={t("studio.providers.empty.description")}
            action={
              <Button size="sm" onClick={() => setSheetOpen(true)}>
                <Plus />
                {t("studio.providers.new")}
              </Button>
            }
          />
        }
      />
      <ProviderSheet
        open={open}
        onOpenChange={setOpen}
        provider={editing}
        onCreated={(p) => {
          void runTest(p)
        }}
      />
      <ImportModelsDialog provider={importing} onOpenChange={(o) => !o && setImporting(undefined)} />
    </PageContainer>
  )
}
